import styled, { css } from "styled-components";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as Svg12 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";

// test ids
import { TURBO_STRIP_TEST_IDS } from "@swiggy-private/payment-test-ids";

// animations
import { OSCILLATE, SLIDE_STRIP, SLIDE_UP, SLIDE_DOWN } from "./animations";
import AriaHidden from "@core-ui/components/accessibility/aria-hidden/AriaHidden";
import { ANIMATION_DURATION } from "./constants";
import { TurboStripType } from "@core-ui/interfaces/Turbo";

export const StripText = styled(Text).attrs({
    "data-testid": TURBO_STRIP_TEST_IDS.STRIP_MSG,
})<{ type: TurboStripType }>`
    margin-left: ${({ theme }) => `${theme.spacing.xSmall}`};
    color: ${({ theme }) => theme.colors[theme.colorMode].text_High_Emphasis};

    ${({ type }) =>
        type === TurboStripType.STATIC_INVERSE &&
        css`
            margin-left: 0 !important;
            color: ${({ theme }) => theme.colors[theme.colorMode].primary_Accent};
        `}
`;

export const AnimatedTextContainer = styled.div<{ isAnimating?: boolean }>`
    overflow: hidden;
    margin-left: ${({ theme }) => `${theme.spacing.xSmall}`};
    height: 18px;
    position: relative;
`;

const getTransformY = ({ isActive, isExiting }: { isActive: boolean; isExiting: boolean }) => {
    if (isActive) return "0";
    if (isExiting) return "-100%";
    return "100%";
};

const getAnimation = ({ isActive, isExiting }: { isActive: boolean; isExiting: boolean }) => {
    if (isActive) {
        return css`
            ${SLIDE_DOWN} ${ANIMATION_DURATION}ms ease-out forwards
        `;
    }
    if (isExiting) {
        return css`
            ${SLIDE_UP} ${ANIMATION_DURATION}ms ease-out forwards
        `;
    }
    return "none";
};

export const AnimatedText = styled(Text).attrs({
    "data-testid": TURBO_STRIP_TEST_IDS.ANIMATED_TEXT,
})<{ isActive: boolean; isExiting: boolean }>`
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    animation: ${getAnimation};
    white-space: nowrap;
    opacity: ${({ isActive, isExiting }) => (isActive || isExiting ? 1 : 0)};
    transform: translateY(${getTransformY});
`;

export const InfoWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
    min-width: 0;
`;

export const StripWrapperParent = styled.div<{ type?: TurboStripType }>`
    ${({ type }) =>
        type === TurboStripType.DYNAMIC_SPLIT_PAY_INFO &&
        css`
            padding: ${({ theme }) => theme.spacing.xxSmall};
        `}
`;

export const StripWrapper = styled.div.attrs({
    "data-testid": TURBO_STRIP_TEST_IDS.WRAPPER,
})<{ isTurboV2?: boolean; type?: TurboStripType; isTurbo?: boolean }>`
    background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Secondary};
    padding: ${({ theme }) => `8px ${theme.spacing.medium}`};
    position: relative;
    color: ${({ theme }) => theme.colors[theme.colorMode].text_Med_Emphasis};
    overflow: hidden;
    margin: ${({ theme }) => theme.spacing.xxSmall};
    margin-bottom: 0;
    border-radius: ${({ theme }) => `${theme.spacing.medium} ${theme.spacing.medium} 0 0`};

    ${({ isTurboV2 }) =>
        isTurboV2 &&
        css`
            border-radius: 0;
        `}

    ${({ type }) =>
        type === TurboStripType.STATIC_INVERSE &&
        css`
            background: linear-gradient(
                -90deg,
                ${({ theme }) => theme.colors[theme.colorMode].background_Primary} -70.01%,
                ${({ theme }) => theme.colors[theme.colorMode].rain_Sub} 101.74%
            );
            border-radius: 0;
            border-top: 1px solid ${({ theme }) => theme.colors[theme.colorMode].border_Tertiary};
            margin: -${({ theme }) => theme.spacing.medium} -${({ theme }) => theme.spacing.medium}
                ${({ theme }) => theme.spacing.medium} -${({ theme }) => theme.spacing.medium};
        `}

    ${({ isTurbo }) =>
        isTurbo &&
        css`
            margin: 0;
        `}

    ${({ type }) =>
        type === TurboStripType.DYNAMIC_SPLIT_PAY_INFO &&
        css`
            border-radius: ${({ theme }) => `${theme.spacing.medium} ${theme.spacing.medium} 0 0`};
        `}
`;

export const StripContent = styled.div`
    display: flex;
    align-items: center;
    z-index: 2;
    justify-content: space-between;
    position: relative;
`;

export const RightContent = styled.div`
    display: flex;
    position: relative;
    animation: ${OSCILLATE} 1s ease-out infinite;
`;

export const Strip = styled.div`
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: ${(props) => props.theme.spacing.large};
    background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Primary};
    transform: rotate(62deg);
    animation: ${SLIDE_STRIP} 4s cubic-bezier(0.65, 0, 0.35, 1) 3;
    box-shadow: 0 0 20px 8px rgb(255 255 255 / 97%);
    z-index: 2;
    filter: ${(props) => `blur(${props.theme.spacing.xxSmall})`};
    opacity: 0.6;
`;

export const Chevron = styled(Svg12).attrs({
    "data-testid": TURBO_STRIP_TEST_IDS.RIGHT_ARROW,
})`
    fill: unset;
    position: absolute;
    left: -7px;
    & > path {
        fill-opacity: 0.4;
    }
`;

export const AriaHiddenAnimatedText = styled(AriaHidden)`
    flex-grow: 1;
`;

export const CtaTextWrapper = styled(Text).attrs({
    "data-testid": TURBO_STRIP_TEST_IDS.CTA_TEXT,
})`
    background-color: ${({ theme }) => theme.colors[theme.colorMode].primary_Accent};
    padding: ${({ theme }) => `5px ${theme.spacing.small}`};
    border-radius: ${({ theme }) => `${theme.cornerRadius.halfRounded}`};
    border: 1px solid ${({ theme }) => theme.colors[theme.colorMode].divider};
    max-width: fit-content;
    margin: 0 0 0 ${({ theme }) => theme.spacing.small};
`;
